import { Link } from "react-router-dom";
import { ArrowRight, Leaf, HeartHandshake, Compass } from "lucide-react";
import { useSeo } from "@/lib/seo";
import Reveal from "@/components/site/Reveal";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxjb2xsYWJvcmF0aXZlJTIwb2ZmaWNlJTIwbWVldGluZyUyMGRpdmVyc2V8ZW58MHx8fHwxNzgyMzk4NzA2fDA&ixlib=rb-4.1.0&q=85";

const values = [
  {
    Icon: Leaf,
    title: "Human-first",
    body: "We treat policy as a tool to make work better — not a shield against people.",
  },
  {
    Icon: HeartHandshake,
    title: "Built alongside you",
    body: "Knowledge transfer is woven into every engagement. We leave you stronger than we found you.",
  },
  {
    Icon: Compass,
    title: "Grounded in Ontario",
    body: "ESA, OHRC, Labour Relations Act — applied with judgment and context, not boilerplate.",
  },
];

const team = [
  {
    name: "Jordan Reeve",
    role: "Founder & Principal Consultant",
    bio: "Twenty years of HR leadership across unionized manufacturing, healthcare, and growing tech teams.",
  },
  {
    name: "Sana Okafor",
    role: "Labour Relations Lead",
    bio: "Former union rep turned management-side advisor. Brings calm, prepared bargaining to every table.",
  },
  {
    name: "Marc Lévesque",
    role: "Leadership Coach",
    bio: "ICF-certified coach focused on first-time managers and owner-operators navigating people work.",
  },
];

export default function About() {
  useSeo({
    title: "About",
    description:
      "Meet The People Collective — an Ontario HR consultancy built on human-first values, deep labour relations expertise, and a partnership-not-vendor approach.",
    path: "/about",
  });

  return (
    <div data-testid="page-about">
      {/* Intro */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <p className="tpc-eyebrow">About us</p>
              <h1 className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
                A small collective, deeply committed to your{" "}
                <span className="italic text-[#82917B]">people work.</span>
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-7 text-lg text-[#4A5348] leading-relaxed">
                The People Collective was founded on a simple frustration:
                most HR support shows up with a binder and disappears.
                We wanted something different — a partner who sat with the
                team, learned the room, and stayed long enough that the
                knowledge actually stuck.
              </p>
              <p className="mt-5 text-lg text-[#4A5348] leading-relaxed">
                Today, we work alongside Ontario businesses navigating their
                first hires, their first collective agreement, their hardest
                conversations, and their next stage of growth.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <Reveal delay={0.12}>
              <div className="relative">
                <div className="absolute -inset-4 bg-[#F3EFE7] -z-10" />
                <img
                  src={ABOUT_IMG}
                  alt="A small team in conversation at a warm, open meeting space"
                  className="w-full h-[440px] md:h-[560px] object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#F3EFE7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <Reveal>
              <p className="tpc-eyebrow">What we believe</p>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight">
                Three values that shape every engagement.
              </h2>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((v, i) => {
              const Icon = v.Icon;
              return (
                <Reveal key={v.title} delay={i * 0.08}>
                  <div
                    data-testid={`about-value-${i}`}
                    className="h-full p-8 bg-[#FDFBF7] border border-[#82917B]/20"
                  >
                    <Icon size={32} className="text-[#82917B]" />
                    <p className="mt-6 font-serif text-2xl text-[#2B3329]">
                      {v.title}
                    </p>
                    <p className="mt-3 text-[#4A5348] leading-relaxed text-[15px]">
                      {v.body}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="tpc-eyebrow">Our approach</p>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight">
                Three quiet steps. No surprises.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-10">
            {[
              {
                step: "01",
                title: "Listen first",
                body: "We spend real time with leaders, managers, and frontline employees before we propose anything.",
              },
              {
                step: "02",
                title: "Co-create",
                body: "Policies, frameworks, and bargaining strategies are built with your team — not handed down.",
              },
              {
                step: "03",
                title: "Step back",
                body: "We build your team’s confidence and capacity so we can scale back as soon as you’re ready.",
              },
            ].map((s, i) => (
              <Reveal key={s.step} delay={i * 0.08}>
                <div className="grid grid-cols-12 gap-6 items-start border-t border-[#82917B]/20 pt-8">
                  <div className="col-span-3 font-serif text-5xl text-[#82917B]">
                    {s.step}
                  </div>
                  <div className="col-span-9">
                    <p className="font-serif text-2xl text-[#2B3329]">
                      {s.title}
                    </p>
                    <p className="mt-2 text-[#4A5348] text-[15px] leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 md:py-32 bg-[#F3EFE7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <Reveal>
              <p className="tpc-eyebrow">The collective</p>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight">
                A small bench. Real depth.
              </h2>
            </Reveal>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {team.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.08}>
                <div
                  data-testid={`about-team-${i}`}
                  className="h-full p-8 bg-[#FDFBF7] border border-[#82917B]/20"
                >
                  <div className="h-16 w-16 rounded-full bg-[#82917B] text-white flex items-center justify-center font-serif text-2xl">
                    {m.name
                      .split(" ")
                      .map((w) => w[0])
                      .join("")}
                  </div>
                  <p className="mt-6 font-serif text-2xl text-[#2B3329]">
                    {m.name}
                  </p>
                  <p className="mt-1 text-sm tracking-wide uppercase text-[#82917B]">
                    {m.role}
                  </p>
                  <p className="mt-4 text-[#4A5348] leading-relaxed text-[15px]">
                    {m.bio}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight">
              Curious whether we’re the right fit?
            </h2>
            <p className="mt-5 text-[#4A5348] text-lg">
              Start with a no-pressure conversation. We’ll be honest about
              whether we can help — or who else should.
            </p>
            <Link
              to="/contact"
              data-testid="about-cta-book"
              className="tpc-btn tpc-btn-primary mt-8"
            >
              Book a Consultation <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
